import {
  ActivityTypeCode,
  CouponTypeCode,
  Whether,
} from "project-config/constants";
import { BalanceTipType, ExtraWelfareTipType, RechargeInfo } from "../types";
import { PictureFirstRechargeInfo } from "../constants/picture-balance-info";
import { couponTypeTranslate } from "@/components/top-up/help/couponTypeTranslate";
import { getTranslateNsT } from "@/utils/getTranslateNsT";
import {
  findWelfareData,
  presentAmountCalculate,
} from "@/components/top-up/help/activityUtil";
import { initVerifyRechargeInfo } from "../store";

const t = getTranslateNsT("topup");

export enum BalanceWelfareType {
  balance = 1,
}
export enum ExtraWelfareType {
  GPU = CouponTypeCode.GPU,
  CPU = CouponTypeCode.CPU,
  Universal = CouponTypeCode.Universal,
  VIP = 4,
  Desktop = CouponTypeCode.Desktop,
}

export class RechargeTips {
  private rechargeInfo: RechargeInfo = {
    amount: 0,
    userProperty: {
      isReRegister: 0,
      picRechargeOver: 0,
      isFirstRecharge: 0,
      isFocusOnWX: 0,
      isBindMiniPro: 0,
      isRealName: 0,
    },
    currencySymbol: "",
    activityInfo: {
      id: 0,
      activityType: -1,
      activityName: "",
      activityDetail: [],
      activityNameEn: "",
    },
    activityDetailItem: {},
    rechargeSecondActiveName: "",
    verifyRechargeInfo: initVerifyRechargeInfo,
  };

  public balanceTip: BalanceTipType[] = [
    {
      type: BalanceWelfareType.balance,
      content: "",
    },
  ];

  public extraWelfareTip: ExtraWelfareTipType[] = [
    {
      type: ExtraWelfareType.GPU,
      content: [],
      pre: couponTypeTranslate(ExtraWelfareType.GPU),
      contentPre: "￥",
    },
    {
      type: ExtraWelfareType.CPU,
      content: [],
      pre: couponTypeTranslate(ExtraWelfareType.CPU),
      contentPre: "￥",
    },
    {
      type: ExtraWelfareType.Universal,
      content: [],
      pre: couponTypeTranslate(ExtraWelfareType.Universal),
      contentPre: "￥",
    },
    {
      type: ExtraWelfareType.VIP,
      content: [],
      pre: t("activity_member_free_member_vip"),
      contentSuf: t("activity_member_free_member_month"),
    },
    {
      type: ExtraWelfareType.Desktop,
      content: [],
      pre: couponTypeTranslate(ExtraWelfareType.Desktop),
      contentPre: "￥",
    },
  ];

  private balance = this.balanceTip.find(
    (item) => item.type === BalanceWelfareType.balance
  );
  private universal = this.extraWelfareTip.find(
    (item) => item.type === ExtraWelfareType.Universal
  );
  private vip = this.extraWelfareTip.find(
    (item) => item.type === ExtraWelfareType.VIP
  );
  private desktop = this.extraWelfareTip.find(
    (item) => item.type === ExtraWelfareType.Desktop
  );

  constructor(rechargeInfo: RechargeInfo) {
    this.rechargeInfo = rechargeInfo;
  }

  public init() {
    this.rechargeAmount();
    this.firstRechargeTips();
    this.newUserTips();
    this.rechargeGiftTips();
    this.GiftMemberTips();
    this.desktopTips();
    this.rechargeExchange();
  }

  // 余额到账
  private rechargeAmount() {
    const { currencySymbol, amount } = this.rechargeInfo;
    if (this.balance) {
      this.balance.content = `${currencySymbol}${amount}`;
    }
  }

  // 首充福利
  private firstRechargeTips() {
    const { amount, activityInfo, userProperty, rechargeSecondActiveName } =
      this.rechargeInfo;
    if (
      amount <= 0 ||
      activityInfo?.activityType === ActivityTypeCode.GiftMember ||
      rechargeSecondActiveName !== "second-tab-first-recharge"
    )
      return;
    if (userProperty.isFirstRecharge === Whether.No) {
      const findRechargeIndex = PictureFirstRechargeInfo.findIndex(
        (p) => p.price > amount
      );
      const tip =
        findRechargeIndex > 0
          ? PictureFirstRechargeInfo[findRechargeIndex - 1].giftAmount
          : findRechargeIndex === 0
          ? 0
          : PictureFirstRechargeInfo[PictureFirstRechargeInfo.length - 1]
              .giftAmount;
      if (this.universal && tip) {
        this.universal.content.push(tip);
      }
    }
  }

  // 新用户福利（15天内新注册用户单笔满100送25通用券）
  private newUserTips() {
    const { amount, activityInfo, userProperty } = this.rechargeInfo;
    if (
      amount <= 0 ||
      activityInfo?.activityType === ActivityTypeCode.GiftMember
    )
      return;
    if (userProperty.picRechargeOver === Whether.No && amount >= 100) {
      if (this.universal) {
        this.universal.content.push(25);
      }
    }
  }

  // 充值赠送活动
  private rechargeGiftTips() {
    const { amount, activityInfo, rechargeSecondActiveName } =
      this.rechargeInfo;
    if (amount <= 0 || rechargeSecondActiveName === "second-tab-first-recharge")
      return;
    if (activityInfo?.activityType === ActivityTypeCode.RechargeGift) {
      const welfareData = findWelfareData(activityInfo, amount);
      let welfareAmount = null;
      let welfareCouponType: number | null | undefined = null;
      let welfareVipPresentAmount = null;
      if (welfareData) {
        welfareAmount = presentAmountCalculate(
          activityInfo,
          amount,
          welfareData.presentAmount
        );
        welfareCouponType = welfareData.couponType;
        welfareVipPresentAmount = welfareData.vipPresentAmount;
        if (welfareAmount) {
          const extra = this.extraWelfareTip.find(
            (item) => item.type === welfareCouponType
          );
          if (extra) {
            extra.content.push(welfareAmount);
          }
        }
        if (welfareVipPresentAmount && this.vip) {
          this.vip.content.push(welfareVipPresentAmount);
        }
      }
    }
  }

  // 赠送会员活动（效果图购买会员送会员）
  private GiftMemberTips() {
    const { activityInfo, activityDetailItem } = this.rechargeInfo;
    if (
      activityInfo?.activityType === ActivityTypeCode.GiftMember &&
      activityDetailItem &&
      this.vip
    ) {
      // 效果图会员订阅展示最终总的会员月数
      this.vip.content.push(
        (activityDetailItem.vipAmount || 0) +
          (activityDetailItem.vipPresentAmount || 0)
      );
    }
  }

  // 桌面充值活动
  private desktopTips() {
    const { activityInfo, amount } = this.rechargeInfo;
    if (activityInfo?.activityType === ActivityTypeCode.Desktop) {
      const detail = activityInfo.activityDetail.find(
        (item) => item.amount === amount
      );
      if (this.desktop) {
        if (detail) {
          this.desktop.content.push(detail.presentAmount || amount);
        } else {
          this.desktop.content.push(amount);
        }
      }
    }
  }

  // picture充值兑换
  private rechargeExchange() {
    const { amount, verifyRechargeInfo } = this.rechargeInfo;
    if (!verifyRechargeInfo) return;
    const { couponNO, rechargeAmount, couponAmount, couponType } =
      verifyRechargeInfo;
    const extra = this.extraWelfareTip.find((item) => item.type === couponType);
    if (couponNO && amount >= rechargeAmount && this.universal && extra) {
      extra.content.push(couponAmount);
    }
  }
}
